import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "filename",
    "uploadedFilename",
    "uploadedFile",
    "dropzone",
    "removeFlag",
    "image",
    "preview",
    "deleteButton"
  ]

  connect() {
    this._dragOverHandler = this.dragOver.bind(this)
    this._dragLeaveHandler = this.dragLeave.bind(this)
    this._dropHandler = this.drop.bind(this)
    this._changeHandler = this.change.bind(this)

    this.element.addEventListener("dragover", this._dragOverHandler)
    this.element.addEventListener("dragleave", this._dragLeaveHandler)
    this.element.addEventListener("drop", this._dropHandler)
    this.inputTarget.addEventListener("change", this._changeHandler)

    this.updateFileDisplay()
  }

  disconnect() {
    this.element.removeEventListener("dragover", this._dragOverHandler)
    this.element.removeEventListener("dragleave", this._dragLeaveHandler)
    this.element.removeEventListener("drop", this._dropHandler)
    this.inputTarget.removeEventListener("change", this._changeHandler)
  }

  dragOver(event) {
    event.preventDefault()
    this.element.classList.add("file--is-dragging")
  }

  dragLeave() {
    this.element.classList.remove("file--is-dragging")
  }

  drop(event) {
    event.preventDefault()
    this.element.classList.remove("file--is-dragging")
    this.inputTarget.files = event.dataTransfer.files
    this.change()
  }

  change() {
    // Re-uploading clears any pending removal mark.
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "0"
    }
    this.renderClientSidePreview()
    this.updateFileDisplay()
  }

  // Marks the existing signature for purge on next form save. The actual
  // purge runs server-side via Account#after_save_commit when the form
  // submits with `account[remove_signature]=1`. Visually we hide the image,
  // reveal the dropzone for potential re-upload, and re-render the wrapper
  // as "no attachment" so the hover-delete button is no longer relevant.
  markForRemoval() {
    if (this.hasRemoveFlagTarget) {
      this.removeFlagTarget.value = "1"
    }
    // Clear any client-picked file so the form submits as "no signature".
    this.inputTarget.value = ""

    if (this.hasImageTarget) this.imageTarget.classList.add("d-none")
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("d-none")
      this.previewTarget.removeAttribute("src")
    }
    this.dropzoneTarget.classList.remove("invisible")
    this.dropzoneTarget.setAttribute("data-signature-attached", "false")
    this.element.classList.remove("file__signature--has-delete")
    this.hideDeleteButton()
    this.updateFileDisplay()
  }

  // Read the file the user just picked and render its data URL into the
  // preview <img>. Avoids waiting for the server-side variant to generate
  // after save, and gives instant feedback ("yes, I picked the right file").
  renderClientSidePreview() {
    if (!this.hasPreviewTarget || this.inputTarget.files.length === 0) return

    const file = this.inputTarget.files[0]
    if (!file.type.startsWith("image/")) return

    const reader = new FileReader()
    reader.onload = (event) => {
      this.previewTarget.src = event.target.result
      this.previewTarget.classList.remove("d-none")
      if (this.hasImageTarget) this.imageTarget.classList.add("d-none")
      this.dropzoneTarget.classList.add("invisible")
      this.element.classList.add("file__signature--has-delete")
      this.showDeleteButton()
    }
    reader.readAsDataURL(file)
  }

  showDeleteButton() {
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.remove("d-none")
    }
  }

  hideDeleteButton() {
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.add("d-none")
    }
  }

  updateFileDisplay() {
    const isAttached = this.dropzoneTarget.getAttribute("data-signature-attached") === "true"
    const hasFile = this.inputTarget.files.length > 0

    if (hasFile) {
      this.filenameTarget.classList.add("d-none")
      this.uploadedFilenameTarget.classList.remove("d-none")
      this.uploadedFileTarget.textContent = this.inputTarget.files[0].name
    } else {
      this.filenameTarget.classList.remove("d-none")
      this.uploadedFilenameTarget.classList.add("d-none")
      this.uploadedFileTarget.textContent = ""
      if (!isAttached) {
        this.dropzoneTarget.classList.remove("invisible")
      } else {
        this.dropzoneTarget.classList.add("invisible")
      }
    }
  }
}
